import { API_CONFIG } from "../config/api";
import { StockData, StockQuote } from "../components/stock/types";
import { sanitizeStockAnalysisPayload } from "./stockPayload";

const BASE_URL = API_CONFIG.BASE_URL;

export type StockApiErrorCode =
  | "quote_failed"
  | "analysis_failed"
  | "not_found"
  | "upgrade_required"
  | "network";

export class StockApiError extends Error {
  readonly code: StockApiErrorCode;

  constructor(code: StockApiErrorCode, message?: string) {
    super(message ?? code);
    this.name = "StockApiError";
    this.code = code;
  }
}

async function parseErrorBody(response: Response): Promise<Record<string, unknown>> {
  try {
    return (await response.json()) as Record<string, unknown>;
  } catch {
    return {};
  }
}

function errorFromResponse(
  response: Response,
  body: Record<string, unknown>,
  fallback: StockApiErrorCode
): StockApiError {
  const code = String(body.code ?? "").toUpperCase();
  if (response.status === 404) {
    return new StockApiError("not_found");
  }
  if (code === "UPGRADE_REQUIRED" || response.status === 402) {
    return new StockApiError(
      "upgrade_required",
      typeof body.message === "string" ? body.message : undefined
    );
  }
  if (response.status >= 400 && response.status < 500) {
    return new StockApiError(fallback);
  }
  return new StockApiError(fallback);
}

function wrapFetchError(error: unknown, fallback: StockApiErrorCode): StockApiError {
  if (error instanceof StockApiError) {
    return error;
  }
  if (error instanceof TypeError) {
    return new StockApiError("network");
  }
  return new StockApiError(fallback);
}

export const fetchStockStatistics = async (symbol: string, token?: string | null) => {
  const headers: Record<string, string> = {};
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  const response = await fetch(
    `${BASE_URL}/stock/${encodeURIComponent(symbol)}/statistics`,
    { headers },
  );

  if (!response.ok) {
    throw new Error(`Failed to fetch stock statistics for ${symbol}`);
  }

  return await response.json();
};

export const fetchGraphData = async (
  symbol: string,
  timeframe: "1D" | "1M" | "3M" | "1Y" | "ALL",
  token?: string | null,
) => {
  const headers: Record<string, string> = {};
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  const response = await fetch(
    `${BASE_URL}/stock/${encodeURIComponent(symbol)}/graph?timeframe=${encodeURIComponent(timeframe)}`,
    { headers },
  );

  if (!response.ok) {
    throw new Error(`Failed to fetch graph data for ${symbol}`);
  }

  return await response.json();
};

export const fetchStockMeta = async (symbol: string) => {
  const response = await fetch(`${BASE_URL}/stock/${encodeURIComponent(symbol)}/meta`);

  if (!response.ok) {
    throw new Error(`Failed to fetch stock metadata for ${symbol}`);
  }

  return await response.json();
};

export const fetchStockQuote = async (
  token: string | null,
  symbol: string
): Promise<StockQuote> => {
  const headers: Record<string, string> = {};
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  try {
    const response = await fetch(API_CONFIG.STOCK.QUOTE(symbol), { headers });

    if (!response.ok) {
      const body = await parseErrorBody(response);
      throw errorFromResponse(response, body, "quote_failed");
    }

    const data = (await response.json()) as StockQuote;
    return {
      ...data,
      quotedAt: data.quotedAt ?? new Date().toISOString(),
    };
  } catch (error) {
    throw wrapFetchError(error, "quote_failed");
  }
};

export const fetchStockAnalysis = async (
  token: string | null,
  symbol: string
) => {
  const headers: Record<string, string> = {};
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  try {
    const response = await fetch(`${BASE_URL}/stock/${encodeURIComponent(symbol)}`, { headers });

    if (!response.ok) {
      const body = await parseErrorBody(response);
      throw errorFromResponse(response, body, "analysis_failed");
    }

    const payload = await response.json();
    if (payload == null || typeof payload !== "object") {
      throw new StockApiError("analysis_failed");
    }
    return sanitizeStockAnalysisPayload(payload) as unknown as StockData;
  } catch (error) {
    throw wrapFetchError(error, "analysis_failed");
  }
};

function textOf(value: unknown): string {
  if (typeof value === "string") return value.trim();
  return "";
}

function quotedAtFromPricePayload(payload: unknown): string | undefined {
  if (!payload || typeof payload !== "object") return undefined;
  const data = payload as Record<string, unknown>;
  const candidates = [
    data.quotedAt,
    data.quoted_at,
    data.price_timestamp,
    data.priceTimestamp,
    data.as_of,
    data.asOf,
    data.timestamp,
    data.updated_at,
    data.updatedAt,
    data.last_updated,
    data.lastUpdated,
  ];
  for (const value of candidates) {
    const text = textOf(value);
    if (text && !Number.isNaN(new Date(text).getTime())) {
      return text;
    }
  }
  return undefined;
}

function parseBatchPriceEntry(value: unknown): { price: number | null; quotedAt?: string } {
  if (typeof value === "number" && Number.isFinite(value)) {
    return { price: value };
  }
  if (value && typeof value === "object") {
    const record = value as Record<string, unknown>;
    const rawPrice = record.price ?? record.close ?? record.last;
    const price =
      typeof rawPrice === "number" && Number.isFinite(rawPrice)
        ? rawPrice
        : Number(textOf(rawPrice));
    const quotedAt = quotedAtFromPricePayload(record);
    return {
      price: Number.isFinite(price) ? price : null,
      quotedAt,
    };
  }
  const parsed = Number(textOf(value));
  return { price: Number.isFinite(parsed) ? parsed : null };
}

export type StockBatchPrices = {
  prices: Record<string, number>;
  quotedAtBySymbol: Record<string, string | undefined>;
};

/** Batch prices plus per-symbol as-of timestamps when the API returns them. */
export async function fetchStockBatchPrices(
  symbols: string[],
  token?: string | null,
): Promise<StockBatchPrices> {
  if (!symbols || symbols.length === 0) {
    return { prices: {}, quotedAtBySymbol: {} };
  }

  try {
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
    };
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    const response = await fetch(`${BASE_URL}/stock/prices/batch`, {
      method: "POST",
      headers,
      body: JSON.stringify({ symbols }),
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch batch prices: ${response.statusText}`);
    }

    const data = await response.json();
    const raw = (data?.prices ?? {}) as Record<string, unknown>;
    const topLevelQuotedAt =
      data?.quotedAt && typeof data.quotedAt === "object"
        ? (data.quotedAt as Record<string, unknown>)
        : data?.priceTimestamps && typeof data.priceTimestamps === "object"
          ? (data.priceTimestamps as Record<string, unknown>)
          : null;

    const prices: Record<string, number> = {};
    const quotedAtBySymbol: Record<string, string | undefined> = {};

    for (const [symbol, value] of Object.entries(raw)) {
      const parsed = parseBatchPriceEntry(value);
      if (parsed.price != null) {
        prices[symbol] = parsed.price;
        const fromTop = topLevelQuotedAt
          ? quotedAtFromPricePayload(topLevelQuotedAt[symbol] ?? topLevelQuotedAt[symbol.toUpperCase()])
          : undefined;
        quotedAtBySymbol[symbol] = parsed.quotedAt ?? fromTop;
      }
    }

    return { prices, quotedAtBySymbol };
  } catch (error) {
    console.error("Error fetching batch stock prices:", error);
    return { prices: {}, quotedAtBySymbol: {} };
  }
}

export const fetchStockPrices = async (
  symbols: string[],
  token?: string | null,
): Promise<Record<string, number>> => {
  const batch = await fetchStockBatchPrices(symbols, token);
  return batch.prices;
};
